import { useState, useEffect } from 'react';
import { IEvent, IShow } from '@eagerdog/interfaces';
import { apiService } from 'src/services';

interface AKCAgilityClass {
  show_element: string;  // STD, JWW, FAST, T2B, Premier STD, Premier JWW
  level: string;         // Novice A, Novice B, Open, Excellent, Master, Preferred variants
  entries: number;
  jump_heights: {
    [key: string]: number;  // '4': 2, '8': 5, '12': 8, etc.
  };
}

interface AKCAgilityScheduleData {
  classes: AKCAgilityClass[];
  totalEntries: number;
  loaded: boolean;
}

/**
 * Hook to fetch and manage AKC Agility class data for judge schedule building
 */
export const useAKCAgilitySchedule = (event: IEvent, show: IShow) => {
  const [data, setData] = useState<AKCAgilityScheduleData>({
    classes: [],
    totalEntries: 0,
    loaded: false
  });

  useEffect(() => {
    const fetchAgilityClasses = async () => {
      try {
        // Fetch all scores for this agility show
        const scores = await apiService.getEventScores(event._id, {
          show_id: show._id
        } as any);

        // Group by class type (show_element) and level
        const classMap = new Map<string, AKCAgilityClass>();

        scores.forEach((score: any) => {
          // Filter: only include scores that match THIS specific show
          // Handle both string ID and ObjectId object
          const scoreShowId = score.show_id?._id || score.show_id;
          const scoreShowIdStr = scoreShowId?.toString();
          const currentShowIdStr = show._id?.toString();

          if (!score.dog_class || 
              score.show_type !== show.show_type ||
              (scoreShowIdStr && currentShowIdStr && scoreShowIdStr !== currentShowIdStr)) {
            return;
          }

          const key = `${score.dog_class.show_element}-${score.dog_class.level}`;
          
          if (!classMap.has(key)) {
            classMap.set(key, {
              show_element: score.dog_class.show_element,
              level: score.dog_class.level,
              entries: 0,
              jump_heights: {}
            });
          }

          const classData = classMap.get(key)!;
          classData.entries++;

          // Track entries by jump height
          const jumpHeight = score.agility_jump_height || 'Unknown';
          classData.jump_heights[jumpHeight] = (classData.jump_heights[jumpHeight] || 0) + 1;
        });

        const classes = Array.from(classMap.values());
        const totalEntries = classes.reduce((sum, cls) => sum + cls.entries, 0);

        setData({
          classes,
          totalEntries,
          loaded: true
        });

      } catch (error) {
        console.error('Error fetching AKC Agility classes:', error);
        setData({
          classes: [],
          totalEntries: 0,
          loaded: true
        });
      }
    };

    fetchAgilityClasses();
  }, [event._id, show._id]);

  return data;
};

/**
 * Get display label for an AKC Agility class
 */
export const getAKCAgilityClassLabel = (showElement: string, level: string): string => {
  return `${showElement} - ${level}`;
};

/**
 * Get entry count display for an AKC Agility class, including jump height breakdown
 */
export const getAKCAgilityClassCountsDisplay = (
  classData: AKCAgilityClass
): string => {
  const heights = Object.keys(classData.jump_heights).sort((a, b) => {
    // Sort heights: 4, 8, 12, 16, 20, 24, 24C
    const heightOrder = ['4', '8', '12', '16', '20', '24', '24C'];
    return heightOrder.indexOf(a) - heightOrder.indexOf(b);
  });

  const heightCounts = heights
    .map(h => `${h}": ${classData.jump_heights[h]}`)
    .join(', ');

  return `${classData.entries} ${classData.entries === 1 ? 'Entry' : 'Entries'} (${heightCounts})`;
};

/**
 * Calculate estimated running time for an AKC Agility class
 */
export const calculateAKCAgilityClassTime = (
  entries: number,
  showElement: string
): number => {
  // Time per dog in minutes
  const timePerDog: { [key: string]: number } = {
    'Standard': 2.0,
    'JWW': 1.5,
    'FAST': 1.5,
    'T2B': 1.5,
    'Premier Standard': 2.0,
    'Premier JWW': 1.5
  };

  const baseTime = timePerDog[showElement] || 2.0;
  return entries * baseTime;
};

